import * as XLSX from 'xlsx';
import {
  sanitiseStaffId, checkStaffId,
  sanitiseEpf, checkEpf,
  normaliseNic, checkNic,
  checkDateOfBirth, checkDateOfJoining,
  sanitisePhone, checkPhone,
  checkEmail,
  sanitisePostal, checkPostal,
  parseExperience, formatExperience, checkExperience,
  toIso,
} from './employeeValidation';


const fmt = (v) => (v === null || v === undefined ? '' : String(v).trim());

/* ── Choice lists, matching the Add form exactly ──────────────────────────── */

export const TITLE_CHOICES = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Rev'];

export const GENDER_CHOICES = [
  { code: 'M', label: 'Male' },
  { code: 'F', label: 'Female' },
];

export const MARITAL_CHOICES = [
  { code: 'S', label: 'Single' },
  { code: 'M', label: 'Married' },
  { code: 'D', label: 'Divorced' },
  { code: 'W', label: 'Widowed' },
];

export const RELIGION_CHOICES = [
  { code: 'B', label: 'Buddhist' },
  { code: 'C', label: 'Christian' },
  { code: 'H', label: 'Hindu' },
  { code: 'I', label: 'Islam' },
  { code: 'O', label: 'Other' },
];

export const BLOOD_CHOICES = [
  { code: '1', label: 'A+' }, { code: '2', label: 'A-' },
  { code: '3', label: 'B+' }, { code: '4', label: 'B-' },
  { code: '5', label: 'AB+' }, { code: '6', label: 'AB-' },
  { code: '7', label: 'O+' }, { code: '8', label: 'O-' },
];

export const ACCOUNT_TYPE_CHOICES = ['Savings', 'Current'];

/** Accepts either the label ("Male") or the stored code ("M"). */
function codeFromChoices(value, choices) {
  const v = fmt(value).toLowerCase();
  if (!v) return '';
  const hit = choices.find(
    (c) => c.label.toLowerCase() === v || c.code.toLowerCase() === v
  );
  return hit ? hit.code : '';
}

const choiceLabels = (choices) => choices.map((c) => c.label).join(' / ');


export const COLUMNS = [
  // ── Identification ──
  { key: 'serviceNo',  header: 'Staff ID',        required: true,  width: 14, example: 'EMP1001',
    note: 'Max 10 characters. Must not already exist.' },
  { key: 'epfNo',      header: 'EPF No',          required: true,  width: 12, example: 'E1001',
    note: 'Max 5 characters. ETF is saved with the same value.' },
  { key: 'nicNo',      header: 'NIC No',          required: true,  width: 18, example: '199012345678',
    note: '12 digits, or 9 digits + V (e.g. 901234567V).' },
  { key: 'passportNo', header: 'Passport No',     required: false, width: 14, example: '' },

  // ── Name ──
  { key: 'title',      header: 'Title',           required: false, width: 10, example: 'Mr',
    note: TITLE_CHOICES.join(' / ') },
  { key: 'firstName',  header: 'First Name',      required: true,  width: 18, example: 'Kasun' },
  { key: 'middleName', header: 'Middle Name',     required: false, width: 18, example: '' },
  { key: 'lastName',   header: 'Last Name',       required: true,  width: 18, example: 'Perera' },
  { key: 'initials',   header: 'Initials',        required: true,  width: 12, example: 'K.P.',
    note: 'Required. Also used as the bank account holder name.' },
  { key: 'reportName', header: 'Report Name',     required: false, width: 24, example: '',
    note: 'Leave blank to build it from first + middle + last name.' },

  // ── Personal ──
  { key: 'gender',        header: 'Gender',         required: false, width: 10, example: 'Male',
    note: choiceLabels(GENDER_CHOICES) },
  { key: 'dateOfBirth',   header: 'Date of Birth',  required: true,  width: 14, example: '1990-05-14',
    note: 'yyyy-mm-dd. Must be at least 18 years ago.' },
  { key: 'maritalStatus', header: 'Marital Status', required: false, width: 14, example: 'Single',
    note: choiceLabels(MARITAL_CHOICES) },
  { key: 'religion',      header: 'Religion',       required: false, width: 12, example: 'Buddhist',
    note: choiceLabels(RELIGION_CHOICES) },
  { key: 'bloodGroup',    header: 'Blood Group',    required: false, width: 12, example: 'O+',
    note: choiceLabels(BLOOD_CHOICES) },

  // ── Contact ──
  { key: 'mobileNo',      header: 'Mobile No',      required: false, width: 14, example: '0771234567',
    note: 'Numbers only, max 10.' },
  { key: 'homeNo',        header: 'Home Phone',     required: false, width: 14, example: '' },
  { key: 'officeNo',      header: 'Office Phone',   required: false, width: 14, example: '' },
  { key: 'personalEmail', header: 'Personal Email', required: false, width: 26, example: 'kasun@gmail.com',
    note: 'Must contain @.' },
  { key: 'companyEmail',  header: 'Company Email',  required: false, width: 26, example: '' },

  // ── Employment ──
  { key: 'designation', header: 'Designation', required: true,  width: 24, example: 'Senior Managers',
    lookup: 'designations', note: 'Must match a Designation name exactly — see the Reference Lists sheet.' },
  { key: 'department',  header: 'Department',  required: false, width: 24, example: 'Design',
    lookup: 'departments', note: 'See the Reference Lists sheet.' },
  { key: 'location',    header: 'Location',    required: false, width: 20, example: '',
    lookup: 'locations', note: 'See the Reference Lists sheet.' },
  { key: 'grade',       header: 'Grade',       required: false, width: 20, example: '',
    lookup: 'grades', note: 'See the Reference Lists sheet.' },
  { key: 'category',    header: 'Employment Category', required: false, width: 20, example: 'Executive',
    lookup: 'categories', note: 'See the Reference Lists sheet.' },
  { key: 'shift',       header: 'Shift',       required: false, width: 18, example: 'Day Shift',
    lookup: 'shifts', note: 'See the Reference Lists sheet.' },

  { key: 'dateOfJoining',    header: 'Date of Joining',   required: true,  width: 16, example: '2024-01-15',
    note: 'yyyy-mm-dd. Cannot be a future date.' },
  { key: 'noticePeriodDays', header: 'Notice Period Days', required: false, width: 16, example: '30' },
  { key: 'otRate',           header: 'OT Rate',            required: false, width: 12, example: '' },
  { key: 'totalExperience',  header: 'Total Experience',   required: false, width: 16, example: '2Y6M',
    note: 'Years and months, e.g. 2Y6M, 3Y or 6M.' },
  { key: 'costCenter',       header: 'Cost Center',        required: false, width: 18, example: '' },
  { key: 'reportingEmployee', header: 'Reporting Employee', required: false, width: 26, example: '',
    lookup: 'employees',
    note: 'The manager\u2019s Staff ID or name \u2014 see the Reference Lists sheet. Must be an employee that already exists.' },
  { key: 'group',            header: 'Group',              required: false, width: 16, example: '' },

  /* ── Permanent address ──
     The headers were once unqualified ("Address Line 1", "City", "Postal
     Code"). Those older names are still accepted as aliases so a template
     someone filled in before the current-address columns existed still
     imports. */
  { key: 'permanentAddressLine1', header: 'Permanent Address Line 1', required: false, width: 24, example: 'No. 25',
    aliases: ['Address Line 1'] },
  { key: 'permanentAddressLine2', header: 'Permanent Address Line 2', required: false, width: 24, example: 'Galle Road',
    aliases: ['Address Line 2'] },
  { key: 'permanentAddressLine3', header: 'Permanent Address Line 3', required: false, width: 24, example: '',
    aliases: ['Address Line 3'] },
  { key: 'permanentAddressCity',  header: 'Permanent City',           required: false, width: 18, example: 'Colombo',
    aliases: ['City'] },
  { key: 'permanentAddressPostal', header: 'Permanent Postal Code',   required: false, width: 14, example: '10350',
    aliases: ['Postal Code'], note: 'Exactly 5 numbers.' },

  /* ── Current address ──
     Saved as a second address record (type "C"), exactly as the Add form's
     Current Address panel does. Left out entirely when both the first line
     and the city are blank. */
  { key: 'currentAddressLine1', header: 'Current Address Line 1', required: false, width: 24, example: '',
    note: 'Leave the current-address columns blank if it is the same as the permanent address.' },
  { key: 'currentAddressLine2', header: 'Current Address Line 2', required: false, width: 24, example: '' },
  { key: 'currentAddressLine3', header: 'Current Address Line 3', required: false, width: 24, example: '' },
  { key: 'currentAddressCity',  header: 'Current City',           required: false, width: 18, example: '' },
  { key: 'currentAddressPostal', header: 'Current Postal Code',   required: false, width: 14, example: '',
    note: 'Exactly 5 numbers.' },

  // ── Bank ──
  { key: 'bankName',      header: 'Bank',           required: false, width: 22, example: 'Sampath Bank',
    lookup: 'banks', note: 'Only approved banks can be used — see the Reference Lists sheet.' },
  { key: 'branchName',    header: 'Bank Branch',    required: false, width: 22, example: 'Kollupitiya',
    lookup: 'branches', note: 'Must belong to the bank above.' },
  { key: 'accountNumber', header: 'Account Number', required: false, width: 22, example: '100234567890' },
  { key: 'accountType',   header: 'Account Type',   required: false, width: 14, example: 'Savings',
    note: ACCOUNT_TYPE_CHOICES.join(' / ') + '. Blank means Savings.' },

  // ── Emergency contact ──
  { key: 'altContactName',         header: 'Emergency Contact Name',   required: false, width: 22, example: 'Nimal Perera' },
  { key: 'altContactRelationship', header: 'Emergency Relationship',   required: false, width: 18, example: 'Father' },
  { key: 'altContactNumber',       header: 'Emergency Contact No',     required: false, width: 18, example: '0712345678',
    note: 'Numbers only, max 10.' },
  { key: 'altContactAddress',      header: 'Emergency Address',        required: false, width: 24, example: '' },

  // ── Misc ──
  { key: 'remarks', header: 'Remarks', required: false, width: 24, example: '' },
];

export const HEADERS = COLUMNS.map((c) => c.header);

const SECOND_EXAMPLE = {
  serviceNo: 'EMP1002', epfNo: 'E1002', nicNo: '921234567V', firstName: 'Nadeesha',
  lastName: 'Silva', initials: 'N.S.', title: 'Ms', gender: 'Female',
  dateOfBirth: '1992-11-03', maritalStatus: 'Married', religion: 'Christian',
  bloodGroup: 'A+', mobileNo: '0719876543', personalEmail: 'nadeesha@gmail.com',
  designation: 'Senior Managers', department: 'Design', category: 'Executive',
  shift: 'Day Shift', dateOfJoining: '2023-06-01', noticePeriodDays: '30',
  totalExperience: '5Y', permanentAddressLine1: 'No. 8', permanentAddressCity: 'Kandy',
  permanentAddressPostal: '20000',
  currentAddressLine1: 'No. 14/2', currentAddressLine2: 'Temple Road',
  currentAddressCity: 'Colombo', currentAddressPostal: '10250', bankName: 'Sampath Bank', branchName: 'Kollupitiya',
  accountNumber: '100987654321', accountType: 'Current',
  altContactName: 'Sunil Silva', altContactRelationship: 'Father', altContactNumber: '0771112223',
};

/* ── Template ────────────────────────────────────────────────────────────── */

/**
 * Builds the workbook people fill in.
 *
 * @param {Object} lookups  { departments, designations, locations, grades,
 *                            categories, shifts, banks, branches } — each an
 *                            array of { id, name } (branches also carry bankId
 *                            and bankName). Passing the live lists means the
 *                            Reference Lists sheet always shows what the server
 *                            will actually accept.
 */
export function buildTemplateWorkbook(lookups = {}) {
  const wb = XLSX.utils.book_new();

  /* Sheet 1 — the one people type into. */
  const exampleRow = COLUMNS.map((c) => c.example ?? '');
  const secondRow = COLUMNS.map((c) => SECOND_EXAMPLE[c.key] ?? '');
  const ws = XLSX.utils.aoa_to_sheet([HEADERS, exampleRow, secondRow]);
  ws['!cols'] = COLUMNS.map((c) => ({ wch: c.width || 16 }));
  XLSX.utils.book_append_sheet(wb, ws, 'Employees');

  /* Sheet 2 — what every column means. */
  const guide = [
    ['Column', 'Required', 'Format / allowed values'],
    ...COLUMNS.map((c) => [c.header, c.required ? 'Yes' : 'No', c.note || '']),
    [],
    ['How to use this file', '', ''],
    ['1', 'Delete the two example rows before importing.', ''],
    ['2', 'One employee per row. Do not rename, reorder or remove the header row.', ''],
    ['3', 'Dates must be typed as yyyy-mm-dd (e.g. 2024-01-15).', ''],
    ['4', 'Name columns (Department, Designation, Bank…) must match the Reference Lists sheet exactly.', ''],
    ['5', 'Every imported employee is created unapproved and must be approved in the Approvals tab.', ''],
  ];
  const wsGuide = XLSX.utils.aoa_to_sheet(guide);
  wsGuide['!cols'] = [{ wch: 26 }, { wch: 10 }, { wch: 80 }];
  XLSX.utils.book_append_sheet(wb, wsGuide, 'Instructions');

  /* Sheet 3 — the exact names the server knows. */
  // Names only — a code in brackets is accepted too, but the plain name is
  // what people should copy.
  const names = (list) => (list || []).map((x) => x.name);

  const lists = [
    ['Departments', names(lookups.departments)],
    ['Designations', names(lookups.designations)],
    ['Locations', names(lookups.locations)],
    ['Grades', names(lookups.grades)],
    ['Employment Categories', names(lookups.categories)],
    ['Shifts', names(lookups.shifts)],
    ['Banks (approved only)', names(lookups.banks)],
    ['Bank Branches', (lookups.branches || []).map((x) => x.name)],
    ['Branch belongs to Bank', (lookups.branches || []).map((x) => x.bankName || '')],
    ['Titles', TITLE_CHOICES],
    ['Genders', GENDER_CHOICES.map((c) => c.label)],
    ['Marital Statuses', MARITAL_CHOICES.map((c) => c.label)],
    ['Religions', RELIGION_CHOICES.map((c) => c.label)],
    ['Blood Groups', BLOOD_CHOICES.map((c) => c.label)],
    ['Account Types', ACCOUNT_TYPE_CHOICES],
    // Reporting Employee accepts the Staff ID, the name, or this exact
    // "Name (STAFFID)" form — the same label the Add form's dropdown shows.
    ['Reporting Employees', (lookups.employees || []).map((x) => (x.code ? `${x.name} (${x.code})` : x.name))],
  ];
  const height = Math.max(1, ...lists.map(([, v]) => v.length));
  const refRows = [lists.map(([h]) => h)];
  for (let i = 0; i < height; i += 1) {
    refRows.push(lists.map(([, values]) => values[i] ?? ''));
  }
  const wsRef = XLSX.utils.aoa_to_sheet(refRows);
  wsRef['!cols'] = lists.map(() => ({ wch: 26 }));
  XLSX.utils.book_append_sheet(wb, wsRef, 'Reference Lists');

  return wb;
}

export function downloadTemplate(lookups = {}, filename = 'Employee_Import_Template.xlsx') {
  XLSX.writeFile(buildTemplateWorkbook(lookups), filename);
}

/* ── Reading an uploaded file ────────────────────────────────────────────── */

/** Excel serial numbers and Date cells both become yyyy-mm-dd. */
function cellToIso(value) {
  if (value === null || value === undefined || value === '') return '';
  if (value instanceof Date) return toIso(value);
  if (typeof value === 'number') {
    const parsed = XLSX.SSF ? XLSX.SSF.parse_date_code(value) : null;
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }
  return toIso(value);
}

/**
 * @returns {Promise<Array<Object>>} one object per data row, keyed by COLUMNS.key,
 *   plus `_row` (the spreadsheet row number, for error messages).
 */
export async function readWorkbook(file) {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });

  const sheetName = wb.SheetNames.includes('Employees') ? 'Employees' : wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  if (!sheet) throw new Error('The file has no readable sheet.');

  const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, raw: false, defval: '' });
  if (!grid.length) throw new Error('The sheet is empty.');

  const headerRow = grid[0].map((h) => fmt(h).toLowerCase());
  const indexOf = {};
  COLUMNS.forEach((c) => {
    // The current header first, then any older spelling it replaced.
    const names = [c.header, ...(c.aliases || [])];
    let at = -1;
    for (const name of names) {
      at = headerRow.indexOf(name.toLowerCase());
      if (at !== -1) break;
    }
    indexOf[c.key] = at;
  });

  const missing = COLUMNS.filter((c) => c.required && indexOf[c.key] === -1).map((c) => c.header);
  if (missing.length) {
    throw new Error(`The file is missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Download the template and use its header row.`);
  }

  const dateKeys = new Set(['dateOfBirth', 'dateOfJoining']);

  return grid.slice(1).map((cells, i) => {
    const row = { _row: i + 2 };
    COLUMNS.forEach((c) => {
      const at = indexOf[c.key];
      const raw = at === -1 ? '' : cells[at];
      row[c.key] = dateKeys.has(c.key) ? cellToIso(raw) : fmt(raw);
    });
    return row;
  }).filter((row) => COLUMNS.some((c) => row[c.key] !== ''));   // drop blank rows
}


const norm = (v) => fmt(v).replace(/\s*\([^)]*\)\s*$/, '').replace(/\s+/g, ' ').trim().toLowerCase();

export const findByName = (list, name) => {
  const raw = fmt(name);
  if (!raw) return null;
  const v = norm(raw);
  const whole = raw.replace(/\s+/g, ' ').trim().toLowerCase();

  return (list || []).find((x) => {
    if (norm(x.name) === v) return true;
    if (x.code && norm(x.code) === v) return true;
    if (String(x.id).toLowerCase() === v) return true;
    // "Name (CODE)" exactly as the dropdown renders it.
    if (x.code && `${norm(x.name)} (${norm(x.code)})` === whole) return true;
    return false;
  }) || null;
};

/** A few real options, so the error says what WOULD work. */
const sampleOf = (list, limit = 4) => {
  const names = (list || []).map((x) => x.name).filter(Boolean);
  if (!names.length) return 'the list is empty — check that the master data loaded';
  const shown = names.slice(0, limit).join(', ');
  return names.length > limit ? `${shown}, …` : shown;
};

/**
 * @returns {{ values: Object, errors: string[] }}
 *   `values` is the cleaned row with ids resolved; `errors` is empty when the
 *   row can be imported.
 */
export function validateRow(row, lookups = {}, existingServiceNos = new Set()) {
  const errors = [];
  const v = { ...row };

  /* Text rules — the same functions the Add form uses. */
  v.serviceNo = sanitiseStaffId(row.serviceNo);
  const staffErr = checkStaffId(v.serviceNo);
  if (staffErr) errors.push(staffErr);
  else if (existingServiceNos.has(v.serviceNo.toLowerCase())) {
    errors.push(`Staff ID ${v.serviceNo} already exists`);
  }

  v.epfNo = sanitiseEpf(row.epfNo);
  const epfErr = checkEpf(v.epfNo);
  if (epfErr) errors.push(epfErr);

  v.nicNo = normaliseNic(row.nicNo);
  const nicErr = checkNic(v.nicNo);
  if (nicErr) errors.push(nicErr);

  if (!fmt(row.firstName)) errors.push('First name is required');
  if (!fmt(row.lastName)) errors.push('Last name is required');
  if (!fmt(row.initials)) errors.push('Initials are required');

  const dobErr = checkDateOfBirth(row.dateOfBirth);
  if (dobErr) errors.push(dobErr);

  const dojErr = checkDateOfJoining(row.dateOfJoining);
  if (dojErr) errors.push(dojErr);

  ['mobileNo', 'homeNo', 'officeNo', 'altContactNumber'].forEach((key) => {
    v[key] = sanitisePhone(row[key]);
    const label = key === 'altContactNumber' ? 'Emergency contact no' : 'Phone number';
    const err = checkPhone(v[key], label);
    if (err) errors.push(err);
  });

  ['personalEmail', 'companyEmail'].forEach((key) => {
    const err = checkEmail(row[key], key === 'personalEmail' ? 'Personal email' : 'Company email');
    if (err) errors.push(err);
  });

  v.permanentAddressPostal = sanitisePostal(row.permanentAddressPostal);
  const postalErr = checkPostal(v.permanentAddressPostal);
  if (postalErr) errors.push(`Permanent postal code: ${postalErr}`);

  v.currentAddressPostal = sanitisePostal(row.currentAddressPostal);
  const currPostalErr = checkPostal(v.currentAddressPostal);
  if (currPostalErr) errors.push(`Current postal code: ${currPostalErr}`);

  const exp = parseExperience(row.totalExperience);
  const expErr = checkExperience(exp.years, exp.months);
  if (expErr) errors.push(expErr);
  else v.totalExperience = formatExperience(exp.years, exp.months);

  /* Choice columns — blank falls back to the form's default. */
  v.title = TITLE_CHOICES.find((t) => t.toLowerCase() === fmt(row.title).toLowerCase()) || '';
  if (fmt(row.title) && !v.title) errors.push(`Title "${row.title}" is not one of: ${TITLE_CHOICES.join(', ')}`);

  v.gender = codeFromChoices(row.gender, GENDER_CHOICES) || 'M';
  if (fmt(row.gender) && !codeFromChoices(row.gender, GENDER_CHOICES)) {
    errors.push(`Gender "${row.gender}" is not one of: ${choiceLabels(GENDER_CHOICES)}`);
  }

  v.maritalStatus = codeFromChoices(row.maritalStatus, MARITAL_CHOICES) || 'S';
  if (fmt(row.maritalStatus) && !codeFromChoices(row.maritalStatus, MARITAL_CHOICES)) {
    errors.push(`Marital status "${row.maritalStatus}" is not one of: ${choiceLabels(MARITAL_CHOICES)}`);
  }

  v.religion = codeFromChoices(row.religion, RELIGION_CHOICES);
  if (fmt(row.religion) && !v.religion) {
    errors.push(`Religion "${row.religion}" is not one of: ${choiceLabels(RELIGION_CHOICES)}`);
  }

  v.bloodGroup = codeFromChoices(row.bloodGroup, BLOOD_CHOICES) || '7';
  if (fmt(row.bloodGroup) && !codeFromChoices(row.bloodGroup, BLOOD_CHOICES)) {
    errors.push(`Blood group "${row.bloodGroup}" is not one of: ${choiceLabels(BLOOD_CHOICES)}`);
  }

  v.accountType = ACCOUNT_TYPE_CHOICES.find(
    (t) => t.toLowerCase() === fmt(row.accountType).toLowerCase()
  ) || 'Savings';
  if (fmt(row.accountType) && !ACCOUNT_TYPE_CHOICES.some((t) => t.toLowerCase() === fmt(row.accountType).toLowerCase())) {
    errors.push(`Account type "${row.accountType}" is not one of: ${ACCOUNT_TYPE_CHOICES.join(', ')}`);
  }

  /* Name → id. An unknown name is an error, never a silent null. */
  const resolve = (name, listKey, label, required) => {
    const raw = fmt(name);
    if (!raw) {
      if (required) errors.push(`${label} is required`);
      return null;
    }
    const hit = findByName(lookups[listKey], raw);
    if (!hit) {
      errors.push(`${label} "${raw}" was not found. Valid values: ${sampleOf(lookups[listKey])}`);
      return null;
    }
    return hit.id;
  };

  v.designationId = resolve(row.designation, 'designations', 'Designation', true);
  v.departmentId  = resolve(row.department, 'departments', 'Department', false);
  v.locationId    = resolve(row.location, 'locations', 'Location', false);
  v.gradeId       = resolve(row.grade, 'grades', 'Grade', false);
  v.categoryId    = resolve(row.category, 'categories', 'Employment Category', false);
  v.shiftId       = resolve(row.shift, 'shifts', 'Shift', false);
  // The manager must already be an employee — a name we can't resolve is an
  // error rather than a silently dropped link.
  v.reportingEmployeeId = resolve(row.reportingEmployee, 'employees', 'Reporting Employee', false);
  v.group = fmt(row.group);

  /* Bank: branch has to belong to the chosen bank. */
  v.bankId = null;
  v.branchId = null;
  const bankRaw = fmt(row.bankName);
  const branchRaw = fmt(row.branchName);
  if (bankRaw || branchRaw || fmt(row.accountNumber)) {
    const bank = bankRaw ? findByName(lookups.banks, bankRaw) : null;
    if (bankRaw && !bank) errors.push(`Bank "${bankRaw}" was not found among approved banks. Valid values: ${sampleOf(lookups.banks)}`);
    v.bankId = bank ? bank.id : null;

    if (branchRaw) {
      const candidates = (lookups.branches || []).filter(
        (b) => findByName([b], branchRaw) !== null
      );
      const branch = bank
        ? candidates.find((b) => String(b.bankId) === String(bank.id))
        : candidates[0];
      if (!branch) {
        errors.push(
          candidates.length && bank
            ? `Branch "${branchRaw}" does not belong to ${bankRaw}`
            : `Branch "${branchRaw}" was not found among approved branches. Valid values: ${sampleOf(lookups.branches)}`
        );
      }
      v.branchId = branch ? branch.id : null;
    }

    if (fmt(row.accountNumber) && (!v.bankId || !v.branchId)) {
      errors.push('An account number needs both a Bank and a Bank Branch');
    }
  }

  return { values: v, errors };
}

/** Rows whose Staff ID appears more than once in the same file. */
export function findDuplicateServiceNos(rows) {
  const seen = new Map();
  const dupes = new Set();
  rows.forEach((r) => {
    const id = fmt(r.serviceNo).toLowerCase();
    if (!id) return;
    if (seen.has(id)) dupes.add(id);
    seen.set(id, true);
  });
  return dupes;
}


export function buildAddEmployeePayload(v, createdBy, newEmployeeIsApproved) {
  const reportName = fmt(v.reportName) ||
    [v.firstName, v.middleName, v.lastName].filter(Boolean).join(' ');

  return {
    ActionType: 1,
    p_hedServiceNo:            fmt(v.serviceNo),
    p_hedEpfNo:                fmt(v.epfNo),
    p_hedEtfNo:                fmt(v.epfNo),
    p_hedTitle:                fmt(v.title) || 'Mr.',
    p_hedFirstName:            fmt(v.firstName),
    p_hedMiddleName:           fmt(v.middleName),
    p_hedLastName:             fmt(v.lastName),
    p_hedInitials:             fmt(v.initials),
    p_hedReportName:           reportName,
    p_hedGender:               fmt(v.gender),
    p_hedDateOfBirth:          fmt(v.dateOfBirth),
    p_hedNicNo:                normaliseNic(v.nicNo),
    p_hedPassportNo:           fmt(v.passportNo),
    p_hedMaritalStatus:        fmt(v.maritalStatus),
    p_hedReligion:             fmt(v.religion),
    p_hedNationality:          '1',
    p_hedBloodGroup:           fmt(v.bloodGroup) || '7',

    p_hedMobileNo:             fmt(v.mobileNo),
    p_hedHomeNo:               fmt(v.homeNo),
    p_hedOfficeNo:             fmt(v.officeNo),
    p_hedPersonalEmail:        fmt(v.personalEmail),
    p_hedCompanyEmail:         fmt(v.companyEmail),

    p_hedDepartmentId:         v.departmentId ?? null,
    p_hedLocationId:           v.locationId ?? null,
    p_hedDesignationId:        v.designationId ?? null,
    p_hedGradeId:              v.gradeId ?? null,
    p_hedEmploymentCategoryId: v.categoryId ?? null,
    p_hedShiftId:              v.shiftId ?? null,

    p_hedDateOfJoining:        fmt(v.dateOfJoining),
    p_hedConfirmationDate:     '',
    p_hedNoticePeriodDays:     v.noticePeriodDays ? Number(v.noticePeriodDays) : null,
    p_OT:                      v.otRate ? Number(v.otRate) : null,
    p_hedReportingEmployeeId:  v.reportingEmployeeId ? Number(v.reportingEmployeeId) : null,

    p_hedIsSystemUser:         false,
    p_hedCreatedBy:            createdBy,
    p_hedIsActive:             true,
    p_hedRemarks:              fmt(v.remarks),
    p_hedGroup:                fmt(v.group),
    p_hedTotalExperiance:      fmt(v.totalExperience),
    p_hedCostCenter:           fmt(v.costCenter),

    p_heaAddressType:          'P',
    p_heaAddressLine1:         fmt(v.permanentAddressLine1),
    p_heaAddressLine2:         fmt(v.permanentAddressLine2),
    p_heaAddressLine3:         fmt(v.permanentAddressLine3),
    p_heaCity:                 fmt(v.permanentAddressCity),
    p_heaDistrictId:           2,
    p_heaProvinceId:           2,
    p_heaCountryId:            1,
    p_heaPostalCode:           fmt(v.permanentAddressPostal),
    p_heaIsPrimary:            true,
    p_heaCreatedBy:            createdBy,
    p_heaIsActive:             true,
    p_heaRemarks:              '',

    p_hebBankId:               v.bankId ?? null,
    p_hebBranchId:             v.branchId ?? null,
    p_hebAccountName:          fmt(v.initials),
    p_hebAccountNumber:        fmt(v.accountNumber),
    p_hebAccountType:          fmt(v.accountType) || 'Savings',
    p_hebIsPrimary:            true,
    p_hebCreatedBy:            createdBy,
    p_hebIsActive:             true,
    p_hebRemarks:              '',

    p_isApproved:              newEmployeeIsApproved,

    p_hecContactName:          fmt(v.altContactName) || 'N/A',
    p_hecContactNumber:        fmt(v.altContactNumber),
    p_hecRelationship:         fmt(v.altContactRelationship) || 'Other',
    p_hecContactType:          'Emergency',
    p_hecAddress:              fmt(v.altContactAddress),
    p_hecCreatedBy:            createdBy,
    p_hecIsActive:             true,
    p_hecRemarks:              '',

    p_heqQualificationTypeId:  1,
    p_heqQualificationName:    '',
    p_heqFieldOfStudy:         '',
    p_heqInstitute:            '',
    p_heqCountry:              'Sri Lanka',
    p_heqStartDate:            null,
    p_heqCompletedDate:        null,
    p_heqResult:               '',
    p_heqGrade:                '',
    p_heqGPA:                  '',
    p_heqCertificateNo:        '',
    p_heqAttachment:           '',
    p_heqIsHighest:            true,
    p_heqCreatedBy:            createdBy,
    p_heqIsActive:             true,
    p_heqRemarks:              '',
  };
}

/** The employment-detail fix-up the Add form sends after creating the record. */
export function buildEmploymentFixUp(v) {
  const diff = { p_hedServiceNo: fmt(v.serviceNo) };
  if (v.departmentId) diff.p_hedDepartmentId = v.departmentId;
  if (v.categoryId)   diff.p_hedEmploymentCategoryId = v.categoryId;
  if (v.gradeId)      diff.p_hedGradeId = v.gradeId;
  if (v.locationId)   diff.p_hedLocationId = v.locationId;
  if (v.shiftId)      diff.p_hedShiftId = v.shiftId;
  return Object.keys(diff).length > 1 ? diff : null;
}

/**
 * One AddEmployeeAddress payload.
 *
 * @param {'P'|'C'} type  'P' permanent, 'C' current — the same two records the
 *                        Add form creates.
 * @returns {Object|null} null when that address was left blank.
 */
export function buildAddressPayload(v, employeeId, createdBy, type = 'P') {
  const prefix = type === 'C' ? 'currentAddress' : 'permanentAddress';
  const line1 = fmt(v[`${prefix}Line1`]);
  const city  = fmt(v[`${prefix}City`]);
  if (!line1 && !city) return null;

  return {
    p_hedEmployeeId:   employeeId,
    p_heaCreatedBy:    createdBy,
    p_isActive:        'A',
    p_remarks:         '',
    p_heaAddressType:  type,
    p_heaAddressLine1: line1,
    p_heaAddressLine2: fmt(v[`${prefix}Line2`]),
    p_heaAddressLine3: fmt(v[`${prefix}Line3`]),
    p_heaCity:         city,
    p_heaPostalCode:   fmt(v[`${prefix}Postal`]),
  };
}

/** Both addresses, blanks dropped — what the import loop iterates over. */
export function buildAddressPayloads(v, employeeId, createdBy) {
  return ['P', 'C']
    .map((type) => buildAddressPayload(v, employeeId, createdBy, type))
    .filter(Boolean);
}

export function buildBankPayload(v, employeeId, createdBy, newBankIsApproved) {
  if (!v.bankId || !v.branchId || !fmt(v.accountNumber)) return null;
  return {
    p_hebBankId:        Number(v.bankId),
    p_hebBranchId:      Number(v.branchId),
    p_hebAccountName:   fmt(v.initials),
    p_hebAccountNumber: fmt(v.accountNumber),
    p_hebAccountType:   fmt(v.accountType) || 'Savings',
    p_hebIsPrimary:     1,
    p_hebCreatedBy:     createdBy,
    p_hedEmployeeId:    Number(employeeId),
    p_isApproved:       newBankIsApproved,
  };
}

export function buildContactPayload(v, employeeId, createdBy) {
  if (!fmt(v.altContactName)) return null;
  return {
    p_hedEmployeeId:    employeeId,
    p_hecContactName:   fmt(v.altContactName),
    p_hecContactNumber: fmt(v.altContactNumber),
    p_hecRelationship:  fmt(v.altContactRelationship) || 'Other',
    p_hecContactType:   'Emergency',
    p_hecAddress:       fmt(v.altContactAddress),
    p_hecCreatedBy:     createdBy,
    p_hecIsActive:      true,
    p_hecRemarks:       '',
  };
}